// The Reports tab's mobile data mapping (mobile overhaul Phase C §8.1 C).
// At ≤880px the BAS card and the seven-column quarterly table become an
// `.amount` card plus one card per quarter, so the numbers have to come out
// of the same helpers the desktop table uses — `formatMoney` for Invoiced
// and Paid, `fmtCell` ("$0 when zero else exact cents") for the rest.
// Finance.tsx's ReportsTab imports `fmtCell` and `QUARTER_NOTES` from here
// too, so the desktop table and the cards share one source.

import type { QuarterReport } from '../../shared/types';
import { formatMoney, formatMoneyExact } from './moneyFormat';

/** Australian FY quarters — the months they cover and when the BAS is due. */
export const QUARTER_NOTES: Record<string, { months: string; due: string }> = {
  Q1: { months: 'Jul–Sep', due: '28 Oct' },
  Q2: { months: 'Oct–Dec', due: '28 Feb' },
  Q3: { months: 'Jan–Mar', due: '28 Apr' },
  Q4: { months: 'Apr–Jun', due: '28 Jul' },
};

/** "$0" for empty cells, exact cents otherwise (matches the desktop table). */
export function fmtCell(cents: number): string {
  return cents === 0 ? '$0' : formatMoneyExact(cents);
}

/** "FY27 Q1" → "Q1". */
function quarterPart(key: string): string {
  return key.split(' ')[1] ?? key;
}

/** "FY27 Q1" → "Q1 · Jul–Sep" (just "Q1" if the key isn't one we know). */
export function quarterTitle(key: string): string {
  const part = quarterPart(key);
  const note = QUARTER_NOTES[part];
  return note ? `${part} · ${note.months}` : part;
}

/** The FY note under the BAS card, or null when the key isn't a quarter. */
export function quarterNote(key: string): string | null {
  const part = quarterPart(key);
  const note = QUARTER_NOTES[part];
  return note ? `Figures follow the Australian FY — ${part} runs ${note.months}, due ${note.due}.` : null;
}

/** One figure in a quarter card's 3×2 grid. */
export interface QuarterCell {
  label: string;
  value: string;
  /** Accent (Paid) or gold (Net); the other four take the default ink. */
  tone?: 'a' | 'g';
}

/** The six figures of a quarter card, in the mockup's reading order. */
export function quarterCardRows(q: QuarterReport): QuarterCell[] {
  return [
    { label: 'Invoiced', value: formatMoney(q.invoiced_cents) },
    { label: 'Paid', value: formatMoney(q.paid_cents), tone: 'a' },
    { label: 'Net', value: fmtCell(q.net_cents), tone: 'g' },
    { label: 'GST collected', value: fmtCell(q.gst_collected_cents) },
    { label: 'Expenses', value: fmtCell(q.expenses_cents) },
    { label: 'GST paid', value: fmtCell(q.gst_paid_cents) },
  ];
}

/** The BAS card for the newest quarter of the selected FY. */
export interface BasSummary {
  /** "FY27 Q1 · GST collected". */
  eyebrow: string;
  collected: string;
  gstPaid: string;
  /** Collected minus paid — what the ATO is owed (or owes). */
  position: string;
  /** "28 Oct", or "—" for an unknown quarter key. */
  due: string;
  note: string | null;
}

export function basSummary(latest: QuarterReport): BasSummary {
  return {
    eyebrow: `${latest.key} · GST collected`,
    collected: formatMoneyExact(latest.gst_collected_cents),
    gstPaid: formatMoneyExact(latest.gst_paid_cents),
    position: formatMoneyExact(latest.gst_collected_cents - latest.gst_paid_cents),
    due: QUARTER_NOTES[quarterPart(latest.key)]?.due ?? '—',
    note: quarterNote(latest.key),
  };
}
